//CRN_20260513 - https://riskline.readme.io/reference/apis-overview

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { advisoryDeletedFromHub, advisoryReceivedFromHub, advisoryUpdatedFromHub, alertDeletedFromHub, alertReceivedFromHub, alertUpdatedFromHub, cityReportDeletedFromHub, cityReportReceivedFromHub, countryReportDeletedFromHub, countryReportReceivedFromHub, countryRiskLevelDeletedFromHub, countryRiskLevelReceivedFromHub, executeLocalRiskLevels, executeLocalRiskLevelsFailure, executeLocalRiskLevelsSuccess, executeTravelSearch, executeTravelSearchFailure, executeTravelSearchSuccess, executeTripReady, executeTripReadyFailure, executeTripReadySuccess, fetchAdvisories, fetchAdvisoriesFailure, fetchAdvisoriesSuccess, fetchAdvisoryById, fetchAdvisoryByIdFailure, fetchAdvisoryByIdSuccess, fetchAlerts, fetchAlertsFailure, fetchAlertsSuccess, fetchAllCityReports, fetchAllCityReportsFailure, fetchAllCityReportsSuccess, fetchCityReport, fetchCityReportFailure, fetchCityReportSuccess, fetchCountryReport, fetchCountryReportFailure, fetchCountryReportSuccess, fetchCountryRiskLevels, fetchCountryRiskLevelsFailure, fetchCountryRiskLevelsSuccess, fetchPtaForCountry, fetchPtaForCountryFailure, fetchPtaForCountrySuccess, fetchStaForCountry, fetchStaForCountryFailure, fetchStaForCountrySuccess, fetchSummaryForCountry, fetchSummaryForCountryFailure, fetchSummaryForCountrySuccess, ptaDeletedFromHub, ptaReceivedFromHub, staDeletedFromHub, staReceivedFromHub, summaryDeletedFromHub, summaryReceivedFromHub } from './risk_intelligence_action';
import { AlertFilterDTO } from 'src/app/core/models/system/riskline/riskline-alert.dto';
import { catchError, filter as rxFilter, map, mergeMap, of, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { RisklineAdvisoriesService } from 'src/app/core/services/riskline/riskline-advisories.service';
import { RisklineAlertsService } from 'src/app/core/services/riskline/riskline-alerts.service';
import { RisklineCityReportsService } from 'src/app/core/services/riskline/riskline-city-reports.service';
import { RisklineCountryReportsService } from 'src/app/core/services/riskline/riskline-country-reports.service';
import { RisklineCountryRiskLevelsService } from 'src/app/core/services/riskline/riskline-country-risk-levels.service';
import { RisklineLocalRiskLevelsService } from 'src/app/core/services/riskline/riskline-local-risk-levels.service';
import { RisklinePtasService } from 'src/app/core/services/riskline/riskline-ptas.service';
import { RisklineStaService } from 'src/app/core/services/riskline/riskline-sta.service';
import { RisklineSummariesService } from 'src/app/core/services/riskline/riskline-summaries.service';
import { RisklineTravelSearchService } from 'src/app/core/services/riskline/riskline-travel-search.service';
import { RisklineTripReadyService } from 'src/app/core/services/riskline/riskline-trip-ready.service';

@Injectable()
export class RiskIntelligenceEffects {

  private lastAlertFilter: AlertFilterDTO | undefined;

  //#region Advisories

  public fetchAdvisories$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchAdvisories),
      mergeMap(({ filter }) =>
        this.risklineAdvisoriesService.getAdvisories(filter).pipe(
          map(advisories => fetchAdvisoriesSuccess({ advisories })),
          catchError(error => of(fetchAdvisoriesFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public fetchAdvisoryById$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchAdvisoryById),
      mergeMap(({ id }) =>
        this.risklineAdvisoriesService.getAdvisoryById(id).pipe(
          map(advisory => fetchAdvisoryByIdSuccess({ advisory })),
          catchError(error => of(fetchAdvisoryByIdFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public advisoryReceivedFromHub$=createEffect(() =>
    this.risklineAlertsService.advisoryReceived$.pipe(
      map(advisory => advisoryReceivedFromHub({ advisory }))
    )
  );

  public advisoryUpdatedFromHub$=createEffect(() =>
    this.risklineAlertsService.advisoryUpdated$.pipe(
      map(advisory => advisoryUpdatedFromHub({ advisory }))
    )
  );

  public advisoryDeletedFromHub$=createEffect(() =>
    this.risklineAlertsService.advisoryDeleted$.pipe(
      map(id => advisoryDeletedFromHub({ id }))
    )
  );

  //#endregion

  //#region Alerts

  public fetchAlerts$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchAlerts),
      mergeMap(({ filter }) =>
        this.risklineAlertsService.getAlerts(filter).pipe(
          map(result => fetchAlertsSuccess({ alerts: result.items ?? [] })),
          catchError(error => of(fetchAlertsFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  //CRN_20260508 - SignalR receivers feed actions into the store via these long-lived effects
  public alertReceivedFromHub$=createEffect(() =>
    this.risklineAlertsService.alertReceived$.pipe(
      map(alert => alertReceivedFromHub({ alert }))
    )
  );

  public alertUpdatedFromHub$=createEffect(() =>
    this.risklineAlertsService.alertUpdated$.pipe(
      map(alert => alertUpdatedFromHub({ alert }))
    )
  );

  public alertDeletedFromHub$=createEffect(() =>
    this.risklineAlertsService.alertDeleted$.pipe(
      map(id => alertDeletedFromHub({ id }))
    )
  );

  public rememberLastAlertFilter$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchAlerts),
      tap(({ filter }) => { this.lastAlertFilter=filter; })
    ),
    { dispatch: false }
  );

  public pollFallbackRefetchAlerts$=createEffect(() =>
    this.risklineAlertsService.pollFallbackTick$.pipe(
      rxFilter(() => this.lastAlertFilter!=undefined),
      map(() => fetchAlerts({ filter: <AlertFilterDTO>this.lastAlertFilter }))
    )
  );

  //#endregion

  //#region City Reports

  public fetchCityReport$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCityReport),
      mergeMap(({ iataCode }) =>
        this.risklineCityReportsService.getCityReport(iataCode).pipe(
          map(report => fetchCityReportSuccess({ report })),
          catchError(error => of(fetchCityReportFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public fetchAllCityReports$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchAllCityReports),
      mergeMap(() =>
        this.risklineCityReportsService.getAllCityReports().pipe(
          map(reports => fetchAllCityReportsSuccess({ reports })),
          catchError(error => of(fetchAllCityReportsFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public cityReportReceivedFromHub$=createEffect(() =>
    this.risklineAlertsService.cityReportReceived$.pipe(
      map(report => cityReportReceivedFromHub({ report }))
    )
  );

  public cityReportDeletedFromHub$=createEffect(() =>
    this.risklineAlertsService.cityReportDeleted$.pipe(
      map(iataCode => cityReportDeletedFromHub({ iataCode }))
    )
  );

  //#endregion

  //#region Country Reports

  public fetchCountryReport$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCountryReport),
      mergeMap(({ isoCode }) =>
        this.risklineCountryReportsService.getCountryReport(isoCode).pipe(
          map(report => fetchCountryReportSuccess({ report })),
          catchError(error => of(fetchCountryReportFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public countryReportReceivedFromHub$=createEffect(() =>
    this.risklineAlertsService.countryReportReceived$.pipe(
      map(report => countryReportReceivedFromHub({ report }))
    )
  );

  public countryReportDeletedFromHub$=createEffect(() =>
    this.risklineAlertsService.countryReportDeleted$.pipe(
      map(({ countryIsoCode, language }) => countryReportDeletedFromHub({ countryIsoCode, language }))
    )
  );

  //#endregion

  //#region Country Risk Levels

  public fetchCountryRiskLevels$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCountryRiskLevels),
      mergeMap(() =>
        this.risklineCountryRiskLevelsService.getAllRiskLevels().pipe(
          map(riskLevels => fetchCountryRiskLevelsSuccess({ riskLevels })),
          catchError(error => of(fetchCountryRiskLevelsFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public countryRiskLevelReceivedFromHub$=createEffect(() =>
    this.risklineAlertsService.countryRiskLevelReceived$.pipe(
      map(riskLevel => countryRiskLevelReceivedFromHub({ riskLevel }))
    )
  );

  public countryRiskLevelDeletedFromHub$=createEffect(() =>
    this.risklineAlertsService.countryRiskLevelDeleted$.pipe(
      map(({ countryIsoCode, language }) => countryRiskLevelDeletedFromHub({ countryIsoCode, language }))
    )
  );

  //#endregion

  //#region Local Risk Levels

  public executeLocalRiskLevels$=createEffect(() =>
    this.actions$.pipe(
      ofType(executeLocalRiskLevels),
      mergeMap(({ request }) =>
        this.risklineLocalRiskLevelsService.getLocalRiskLevels(request).pipe(
          map(result => executeLocalRiskLevelsSuccess({ result })),
          catchError(error => of(executeLocalRiskLevelsFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  //#endregion

  //#region PTAs

  public fetchPtaForCountry$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchPtaForCountry),
      mergeMap(({ isoCode }) =>
        this.risklinePtasService.getPtaForCountry(isoCode).pipe(
          map(pta => fetchPtaForCountrySuccess({ pta })),
          catchError(error => of(fetchPtaForCountryFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public ptaReceivedFromHub$=createEffect(() =>
    this.risklineAlertsService.ptaReceived$.pipe(
      map(pta => ptaReceivedFromHub({ pta }))
    )
  );

  public ptaDeletedFromHub$=createEffect(() =>
    this.risklineAlertsService.ptaDeleted$.pipe(
      map(({ countryIsoCode, language }) => ptaDeletedFromHub({ countryIsoCode, language }))
    )
  );
  
  //#endregion

  //#region Specific Traveller Advice

  public fetchStaForCountry$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchStaForCountry),
      mergeMap(({ isoCode }) =>
        this.risklineStaService.getStaForCountry(isoCode).pipe(
          map(sta => fetchStaForCountrySuccess({ sta })),
          catchError(error => of(fetchStaForCountryFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public staReceivedFromHub$=createEffect(() =>
    this.risklineAlertsService.staReceived$.pipe(
      map(sta => staReceivedFromHub({ sta }))
    )
  );

  public staDeletedFromHub$=createEffect(() =>
    this.risklineAlertsService.staDeleted$.pipe(
      map(isoCode => staDeletedFromHub({ isoCode }))
    )
  );

  //#endregion

  //#region Summaries

  public fetchSummaryForCountry$=createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSummaryForCountry),
      mergeMap(({ isoCode }) =>
        this.risklineSummariesService.getSummaryForCountry(isoCode).pipe(
          map(summary => fetchSummaryForCountrySuccess({ summary })),
          catchError(error => of(fetchSummaryForCountryFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  public summaryReceivedFromHub$=createEffect(() =>
    this.risklineAlertsService.summaryReceived$.pipe(
      map(summary => summaryReceivedFromHub({ summary }))
    )
  );

  public summaryDeletedFromHub$=createEffect(() =>
    this.risklineAlertsService.summaryDeleted$.pipe(
      map(({ countryIsoCode, language }) => summaryDeletedFromHub({ countryIsoCode, language }))
    )
  );

  //#endregion

  //#region Travel Search

  public executeTravelSearch$=createEffect(() =>
    this.actions$.pipe(
      ofType(executeTravelSearch),
      mergeMap(({ request }) =>
        this.risklineTravelSearchService.search(request).pipe(
          map(result => executeTravelSearchSuccess({ result })),
          catchError(error => of(executeTravelSearchFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  //#endregion

  //#region TripReady

  public executeTripReady$=createEffect(() =>
    this.actions$.pipe(
      ofType(executeTripReady),
      mergeMap(({ request }) =>
        this.risklineTripReadyService.getJson(request).pipe(
          map(result => executeTripReadySuccess({ result })),
          catchError(error => of(executeTripReadyFailure({ error: error?.message ?? error })))
        )
      )
    )
  );

  //#endregion

  constructor(
    private actions$                        : Actions,
    private risklineAdvisoriesService       : RisklineAdvisoriesService,
    private risklineAlertsService           : RisklineAlertsService,
    private risklineCityReportsService      : RisklineCityReportsService,
    private risklineCountryReportsService   : RisklineCountryReportsService,
    private risklineCountryRiskLevelsService: RisklineCountryRiskLevelsService,
    private risklineLocalRiskLevelsService  : RisklineLocalRiskLevelsService,
    private risklinePtasService             : RisklinePtasService,
    private risklineStaService              : RisklineStaService,
    private risklineSummariesService        : RisklineSummariesService,
    private risklineTravelSearchService     : RisklineTravelSearchService,
    private risklineTripReadyService        : RisklineTripReadyService
  ) { }
}
